import { useMemo, useRef, useState, useEffect } from "react";
import { AlertTriangle, ChevronDown, ChevronUp, Edit } from "lucide-react";
import { Reactable } from "../../components/ui/reactable";
import { PUNCH_DISCREPANCIES } from "../../constants/api";
import { cn } from "../../utils/tailwindUtils";
import { ContainerCard, EmployeeDetails } from "../../components/ui/cards";
import { Button } from "../../components/ui/button";
import { DialogModal } from "../../components/ui/dialogModal";
import AttendanceEdit from "./AttendanceEdit";
import dayjs from "dayjs";
import { useLocation } from "react-router-dom";
import { checkUserPermissionFunc } from "../../components/common/CommonFunctions";
import { useOrgAdminBranchColumn } from "../../hooks/useOrgAdminBranchColumn";

const ISSUE_TABS = [
    { value: "all", label: "All" },
    { value: "single_punch", label: "Single Punch" },
    { value: "odd_punch", label: "Odd Punch" },
    { value: "multi_punch", label: "Multi Punch" },
];

const ISSUE_BADGE = {
    single_punch: "bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300",
    single_punch_today: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300",
    odd_punch: "bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300",
    multi_punch: "bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-300",
};

const ISSUE_SHORT = {
    single_punch: "Single",
    single_punch_today: "In progress",
    odd_punch: "Odd",
    multi_punch: "Multi",
};

const PunchDiscrepancies = () => {
    const location = useLocation();
    const { permission: viewPermission, edit: editPermission } = checkUserPermissionFunc(
        "attendance",
        "Punch Discrepancies",
        "SubMenu"
    );
    const isAdmin = JSON.parse(sessionStorage.getItem("is_admin") || "false");
    const isSuperAdmin = JSON.parse(sessionStorage.getItem("is_super_admin") || "false");
    const canView = viewPermission || isAdmin || isSuperAdmin;
    const canEdit = editPermission || isAdmin || isSuperAdmin;
    const { showBranchColumn, insertBranchColumn, TempTransferBadge } = useOrgAdminBranchColumn("employee_name");
    const [data, setData] = useState([]);
    const [totalRecords, setTotalRecords] = useState(0);
    const [issueType, setIssueType] = useState(location.state?.issueType || "all");
    const [serverTrigger, setServerTrigger] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    const [selectedAttendanceData, setSelectedAttendanceData] = useState(null);
    const [adminEditDialogOpen, setAdminEditDialogOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const attendanceEditRef = useRef();

    const viewFilter = useMemo(() => ({ issue_type: issueType }), [issueType]);

    useEffect(() => {
        if (location.state?.issueType) {
            setIssueType(location.state.issueType);
        }
    }, [location.state?.issueType]);

    const handleSave = async () => {
        try {
            setLoading(true);
            if (attendanceEditRef.current) {
                await attendanceEditRef.current.handleSubmit();
            }
            setAdminEditDialogOpen(false);
            setServerTrigger((prev) => !prev);
        } catch (error) {
            console.error("Save failed:", error);
        } finally {
            setLoading(false);
        }
    };

    const IssueBadges = ({ types = [] }) => (
        <div className="flex flex-wrap gap-1">
            {types.map((type) => (
                <span
                    key={type}
                    className={cn(
                        "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium",
                        ISSUE_BADGE[type] || "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                    )}
                >
                    {ISSUE_SHORT[type] || type}
                </span>
            ))}
        </div>
    );

    const columns = useMemo(() => insertBranchColumn([
        {
            accessor: "employee_name",
            Header: "Employee",
            fixed: true,
            filterable: true,
            maxWidth: "260px",
            Cell: ({ row }) => {
                const record = row.original || row;
                const empDetails = record?.employee_details;
                if (!empDetails) {
                    return (
                        <div className="flex items-center gap-2">
                            <div>
                                <div className="font-medium text-gray-900 dark:text-white">{record?.employee_name || "-"}</div>
                                <div className="text-xs text-gray-500 dark:text-gray-400">{record?.employee_code || "-"}</div>
                            </div>
                            {record?.on_temp_transfer && !showBranchColumn && <TempTransferBadge />}
                        </div>
                    );
                }
                return (
                    <div className="flex items-center gap-2">
                        <EmployeeDetails
                            details={{
                                ...empDetails,
                                first_name: empDetails?.employee_name || record?.employee_name || "-",
                                id: empDetails?.employee_id || record?.employee_code,
                                designation_id: empDetails?.designation
                                    ? {
                                        id: empDetails.designation.id,
                                        designation_name: empDetails.designation.name,
                                    }
                                    : { id: 0, designation_name: "" },
                            }}
                            size="xs"
                        />
                        {record?.on_temp_transfer && !showBranchColumn && <TempTransferBadge />}
                    </div>
                );
            },
        },
        { accessor: "department", Header: "Department", filterable: true, Cell: ({ value }) => value ?? "-" },
        {
            accessor: "date",
            Header: "Date",
            Cell: ({ value }) => (value ? dayjs(value).format("DD MMM YYYY") : "-"),
        },
        { accessor: "shift", Header: "Shift", filterable: true, Cell: ({ value }) => value ?? "-" },
        {
            accessor: "check_in",
            Header: "Check In",
            Cell: ({ value }) => (value && dayjs(value).isValid() ? dayjs(value).format("hh:mm A") : "-"),
        },
        {
            accessor: "check_out",
            Header: "Check Out",
            Cell: ({ value }) => (value && dayjs(value).isValid() ? dayjs(value).format("hh:mm A") : "-"),
        },
        { accessor: "punch_count", Header: "Punches", Cell: ({ value }) => value ?? 0 },
        { accessor: "segment_count", Header: "Segments", Cell: ({ value }) => value ?? 0 },
        {
            accessor: "issue_types",
            Header: "Issue",
            Cell: ({ value }) => <IssueBadges types={value || []} />,
        },
        { accessor: "description", Header: "Notes", Cell: ({ value }) => value ?? "-" },
        {
            accessor: "segments",
            Header: "Detail",
            maxWidth: "90px",
            Cell: ({ row }) => {
                const record = row.original || row;
                const rowId = record?.attendance_id;
                const segments = record?.segments || [];
                if (!segments.length) return "-";
                const isOpen = expandedId === rowId;
                return (
                    <Button
                        variant="ghost"
                        size="sm"
                        className="text-sidebar-primary"
                        onClick={() => setExpandedId(isOpen ? null : rowId)}
                    >
                        {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                        <span className="ml-1 text-xs">{segments.length}</span>
                    </Button>
                );
            },
        },
        ...(canEdit
            ? [{
                accessor: "action",
                Header: "Action",
                fixed: true,
                maxWidth: "70px",
                align: "right",
                Cell: ({ row }) => {
                    const record = row.original || row;
                    return (
                        <Button
                            variant="ghost"
                            size="sm"
                            className="text-sidebar-primary hover:bg-blue-50 dark:hover:bg-gray-700"
                            onClick={() => {
                                setSelectedAttendanceData(record);
                                setAdminEditDialogOpen(true);
                            }}
                        >
                            <Edit className="h-4 w-4" />
                        </Button>
                    );
                },
            }]
            : []),
    ]), [canEdit, expandedId, insertBranchColumn, showBranchColumn, TempTransferBadge]);

    const expandedRow = data.find((row) => row.attendance_id === expandedId);

    if (!canView) {
        return (
            <ContainerCard>
                <div className="flex flex-col items-center justify-center py-24 text-center">
                    <AlertTriangle className="h-10 w-10 text-amber-500 mb-3" />
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Access denied</h2>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md">
                        You do not have permission to view Punch Discrepancies. Ask your administrator to enable
                        Attendance → Punch Discrepancies in User Permissions.
                    </p>
                </div>
            </ContainerCard>
        );
    }

    return (
        <ContainerCard>
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-4 p-3 rounded-lg">
                <div className="flex items-start sm:items-center gap-3 min-w-0 flex-1">
                    <AlertTriangle className="w-6 h-6 text-sidebar-primary flex-shrink-0 mt-0.5 sm:mt-0" />
                    <div className="min-w-0">
                        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                            Punch Discrepancies
                            {totalRecords !== undefined && totalRecords !== null && (
                                <span className="text-muted-foreground font-normal ml-2">
                                    ({totalRecords})
                                </span>
                            )}
                        </h1>
                        <p className="text-sm text-muted-foreground mt-1">
                            Single punch, odd punch count, and multi-segment days
                        </p>
                    </div>
                </div>

                <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden flex-shrink-0 self-start lg:self-center">
                    {ISSUE_TABS.map((tab) => (
                        <button
                            key={tab.value}
                            type="button"
                            onClick={() => setIssueType(tab.value)}
                            className={cn(
                                "px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap",
                                issueType === tab.value
                                    ? "bg-sidebar-primary text-white"
                                    : "bg-white dark:bg-gray-900 text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
                            )}
                        >
                            {tab.label}
                        </button>
                    ))}
                </div>
            </div>

            {expandedRow?.segments?.length > 0 && (
                <div className="mb-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/40 p-4">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 mb-2">
                        Punch segments — {expandedRow.employee_name} ({dayjs(expandedRow.date).format("DD MMM YYYY")})
                    </p>
                    <div className="overflow-x-auto">
                        <table className="min-w-full text-sm">
                            <thead>
                                <tr className="text-left text-gray-500 dark:text-gray-400">
                                    <th className="py-1 pr-4">#</th>
                                    <th className="py-1 pr-4">Check In</th>
                                    <th className="py-1 pr-4">Check Out</th>
                                    <th className="py-1 pr-4">In Via</th>
                                    <th className="py-1 pr-4">Out Via</th>
                                    <th className="py-1">Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {expandedRow.segments.map((segment, index) => (
                                    <tr key={index} className="border-t border-gray-200 dark:border-gray-700">
                                        <td className="py-2 pr-4">{index + 1}</td>
                                        <td className="py-2 pr-4">{segment.check_in || "-"}</td>
                                        <td className="py-2 pr-4">{segment.check_out || "-"}</td>
                                        <td className="py-2 pr-4">{segment.check_in_via || "-"}</td>
                                        <td className="py-2 pr-4">{segment.check_out_via || "-"}</td>
                                        <td className="py-2">
                                            {segment.is_open ? (
                                                <span className="text-amber-600 dark:text-amber-400">Open</span>
                                            ) : (
                                                <span className="text-emerald-600 dark:text-emerald-400">Closed</span>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            <Reactable
                columns={columns}
                data={data}
                setData={setData}
                setTotalCount={setTotalRecords}
                url={PUNCH_DISCREPANCIES}
                serverSide={true}
                serverTrigger={serverTrigger}
                filter={viewFilter}
                printFilter={viewFilter}
                rowSize="sm"
                isCSVRequired={true}
                isPDFRequired={true}
                isFromToDateRequired={true}
            />

            <DialogModal
                open={adminEditDialogOpen}
                onClose={() => {
                    setAdminEditDialogOpen(false);
                    setSelectedAttendanceData(null);
                }}
                onSave={handleSave}
                saveLabel="Edit"
                loading={loading}
                title="Correct Attendance"
                width="72rem"
            >
                <AttendanceEdit
                    editDetails={selectedAttendanceData}
                    ref={attendanceEditRef}
                    onSuccess={() => {
                        setAdminEditDialogOpen(false);
                        setServerTrigger((prev) => !prev);
                    }}
                />
            </DialogModal>
        </ContainerCard>
    );
};

export default PunchDiscrepancies;
